import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Church as ChurchIcon, BookOpen, HeartHandshake, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CHURCH_LOCAL_LIST } from "@/data/churchesLocal";
import { trackPlanVisitClick } from "@/lib/analytics";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const BASE = "https://vcclaychurch.com";

const LOOK_FOR = [
  { icon: BookOpen, title: "The Bible is actually preached", text: "Not opinions, not politics, not self-help with a verse attached. A church worth your family opens the Bible, explains it plainly, and lets it say what it says." },
  { icon: HeartHandshake, title: "Jesus is the center", text: "Programs come and go. The question is whether the church exists to make much of Jesus - because He's the one who changes lives." },
  { icon: Users, title: "You can actually be known", text: "A crowd is easy to find; a family is not. Look for a church small enough in spirit that your absence is noticed and your presence matters." },
  { icon: ChurchIcon, title: "There's room for your kids", text: "If your children dread church, something is off. Look for a place investing in the next generation on purpose, with safety taken seriously." },
];

const FAQS = [
  { q: "How do I choose a church in Clay County?", a: "Visit with three questions in mind: Is the Bible preached clearly? Is Jesus the center? Can my family genuinely belong here? Everything else - music style, building, programs - is secondary to those three." },
  { q: "What kind of church is Victory Chapel?", a: "Victory Chapel is a Bible-centered Christian church in Middleburg, part of Christian Fellowship Ministries - a worldwide fellowship of more than 4,300 churches. We're new to Clay County (launching January 2027), but we stand on decades of proven ministry." },
  { q: "Where is Victory Chapel located?", a: "We're at 3180 CR 220 STE 6-7, Middleburg, FL 32068 - near CR 220 and Henley Road, minutes from Lake Asbury, Oakleaf, Fleming Island, Orange Park, and Green Cove Springs." },
  { q: "When are services?", a: "We launch in January 2027 with Service #1. From launch, we'll gather Sundays at 11:00 AM and Wednesdays at 7:00 PM. Until then, you can plan your first visit online and get connected before doors open." },
];

export default function ChurchesHub() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Church",
        name: "Victory Chapel Clay",
        alternateName: ["Victory Chapel Christian Church", "VC Clay"],
        description: "A new Christian church in Clay County, Florida - launching January 2027 in Middleburg, serving Lake Asbury, Oakleaf, Fleming Island, Orange Park, and Green Cove Springs.",
        url: `${BASE}/churches-in-clay-county-fl`,
        address: {
          "@type": "PostalAddress",
          streetAddress: "3180 CR 220 STE 6-7",
          addressLocality: "Middleburg",
          addressRegion: "FL",
          postalCode: "32068",
          addressCountry: "US",
        },
        telephone: "+1-904-383-2559",
        email: "Victorychapelclay@gmail.com",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Churches in Clay County, FL" },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <>
      <SEO
        title="Churches in Clay County, FL | Find a Church Near You"
        description="Looking for a church in Clay County, FL? What to look for in a church home - plus an intro to Victory Chapel Clay, a new church plant launching January 2027."
        path="/churches-in-clay-county-fl"
        jsonLd={jsonLd}
      />

      <section className="grain relative overflow-hidden bg-navy py-16 text-white sm:py-24">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-navy-light/50 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[360px] w-[360px] rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/60" data-testid="breadcrumb">
            <Link to="/" className="transition-colors hover:text-white">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page" className="text-white/85">Churches in Clay County, FL</span>
          </nav>
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Find a Church</p>
          <LineReveal className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tighter sm:text-6xl" lines={["FINDING A CHURCH IN", "CLAY COUNTY, FL."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              If you've typed "church near me" into a search bar lately, you're not alone - thousands of
              Clay County families are looking for the same thing: a place to belong. Here's an honest
              guide to finding a church home, and an introduction to one being built right now.
            </p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-white/50" data-testid="hub-byline">
              From Victory Chapel Clay &middot; 3180 CR 220 STE 6-7, Middleburg, FL &middot; Launching January 2027
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="hub-look-for-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-balance font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">
              WHAT SHOULD YOU LOOK FOR IN A CHURCH?
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Church buildings all start to look the same from the road. What matters is what's happening
              inside. Four things worth looking for:
            </p>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2">
            {LOOK_FOR.map((c) => (
              <StaggerItem key={c.title}>
                <div className="h-full rounded-3xl border border-navy/10 bg-cream p-7" data-testid={`hub-look-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white">
                    <c.icon size={22} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-800 tracking-tight text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-20" data-testid="hub-about-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-balance font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">
              MEET VICTORY CHAPEL - CLAY COUNTY'S NEWEST CHURCH.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Full disclosure: this is our website, and Victory Chapel is our church - so yes, we're biased.
              But here's why we believe a brand-new church plant in Clay County might be exactly what your
              family has been looking for.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Victory Chapel launches in January 2027 at 3180 CR 220 STE 6-7 in Middleburg, in the heart of
              Clay County. Because we're new, everyone who walks in is a founder - no insiders, no established
              circles, no one who's "been here forever." Just real people building a church family together,
              around a simple mission: Know Jesus. Grow in faith. Go share it.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              When we launch, we'll gather Sundays at 11:00 AM and Wednesdays at 7:00 PM - with heartfelt
              worship, practical Bible preaching, and a VC Kids environment your children will love. Until
              then, we're connecting with families across the county who want to be part of something from
              day one.
            </p>
            <div className="mt-8 flex flex-wrap gap-3.5">
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="hub-plan-visit-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                PLAN YOUR FIRST VISIT <ArrowRight size={15} aria-hidden="true" />
              </Link>
              <Link to="/about" data-testid="hub-about-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-navy/25 px-7 py-3.5 font-display text-sm font-700 text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
                OUR STORY
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="hub-cities-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">Church near you</p>
            <h2 className="mt-3 text-balance font-display text-3xl font-black tracking-tighter text-navy sm:text-4xl">
              FIND US FROM YOUR COMMUNITY.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Wherever you live in Clay County, you're closer than you think. Pick your community for
              directions, details, and what to expect:
            </p>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CHURCH_LOCAL_LIST.map((c) => (
              <StaggerItem key={c.slug}>
                <Link to={`/${c.slug}`} data-testid={`hub-city-${c.slug}`}
                      className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-cream p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white transition-colors group-hover:bg-victory">
                    <MapPin size={22} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-800 tracking-tight text-navy">Church in {c.city}, FL</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{c.intro.split(".")[0]}.</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-display text-xs font-700 text-victory">
                    READ THE LOCAL GUIDE <ArrowRight size={13} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-8 text-center text-sm text-slate-500">
              Also serving: <Link to="/lakeside" className="font-semibold text-victory hover:text-victory-dark">Lakeside</Link> ·{" "}
              <Link to="/keystone-heights" className="font-semibold text-victory hover:text-victory-dark">Keystone Heights</Link> ·{" "}
              <Link to="/doctors-inlet" className="font-semibold text-victory hover:text-victory-dark">Doctors Inlet</Link>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-20" data-testid="hub-faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">COMMON QUESTIONS</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <Accordion type="single" collapsible className="mt-8" data-testid="hub-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`hub-${i}`} className="border-navy/10">
                  <AccordionTrigger data-testid={`hub-faq-trigger-${i}`} className="text-left font-display text-lg font-700 text-navy hover:text-victory">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
